'use client';

import { useEffect, useState } from 'react';
import { Pill } from '@/components/ui/pill';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { useLocale } from 'next-intl';

type SectionHeadingProps = {
    pill?: string;
    title?: string;
    subtitle?: string;
    isVisible?: boolean;
    align?: 'center' | 'left';
    maxWidthClass?: string;
    className?: string;
    page?: string;
};

export function SectionHeading({
    pill: propPill,
    title: propTitle,
    subtitle: propSubtitle,
    isVisible: propIsVisible,
    align = 'center',
    maxWidthClass = 'max-w-3xl',
    className = '',
    page,
}: SectionHeadingProps) {
    const locale = useLocale();
    const [isVisible, setIsVisible] = useState(propIsVisible ?? false);
    const [pill, setPill] = useState(propPill || '');
    const [title, setTitle] = useState(propTitle || '');
    const [subtitle, setSubtitle] = useState(propSubtitle || '');

    useEffect(() => {
        if (page) {
            const fetchSectionHeading = async () => {
                try {
                    const res = await fetch(`/api/page-titles?page=${encodeURIComponent(page)}&locale=${encodeURIComponent(locale)}`, {
                        cache: 'no-store',
                    });
                    if (res.ok) {
                        const data = await res.json();
                        if (data) {
                            setPill(propPill || '');
                            setTitle(propTitle || '');
                            setSubtitle(data.sectionSubtitle || propSubtitle || '');
                        }
                    }
                } catch (error) {

                } finally {
                    setIsVisible(true);
                }
            };

            fetchSectionHeading();
        } else {
            setIsVisible(propIsVisible ?? true);
        }
    }, [page, locale, propPill, propTitle, propSubtitle, propIsVisible]);

    if (!pill && !title && !subtitle) {
        return null;
    }

    const alignClasses = align === 'center' ? 'text-center mx-auto' : 'text-left';

    return (
        <div
            className={cn(
                `${maxWidthClass} ${alignClasses} mb-14 transform transition-all duration-700`,
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6',
                className
            )}
        >
            {pill && (
                <Pill
                    variant={align === 'left' ? 'left' : 'default'}
                    className={cn('mb-4', align === 'center' ? 'mx-auto' : 'mx-0')}
                >
                    {pill}
                </Pill>
            )}
            {title && (
                <Typography variant="h2" className="mb-4">
                    {title}
                </Typography>
            )}
            {subtitle && (
                <Typography variant="p" className="text-lg">
                    {subtitle}
                </Typography>
            )}
        </div>
    );
}

